import { Controller } from '@hotwired/stimulus';

// The sidebar's Current-goals PICKER for the Goals v3 capture control: each
// open goal carries a checkbox, checking lands the goal in the form column's
// Goal data collection section, unchecking takes it back out (nothing is
// saved against an unlanded goal), landed cards highlight, and the summary
// line reads "N of M goals have data".
//
// The picker and the capture control live in different columns, so they talk
// over window events:
//   goals-v3-capture:set           picker -> capture  { goalId, landed }
//   goals-v3-capture:jump          picker -> capture  { goalId }  (scroll to + flash a landed row)
//   goals-v3-capture:state         capture -> picker  { landed: [], withData: [], saved: [], total }
//   goals-v3-capture:request-state picker -> capture  (connect-order safety: whoever
//                                                      connects second still syncs)
//
// `saved` lists goals whose datum already holds saved entries: those are
// locked into the session — the checkbox springs back and jumps instead of
// unlanding (deleting the saved entries on the note is the way out).
//
// Select all (the `all` checkbox) puts every goal into the session, or takes
// every goal out the way the capture's Clear goals does (asking first when a
// goal holds something, keeping goals with saved entries); it reads checked
// while every goal is in, mixed while some are, and the goals' own boxes
// still work one by one.
export default class extends Controller {
  static targets = ['checkbox', 'all', 'summary', 'summaryIcon', 'summaryHint'];

  connect() {
    this._saved = new Set();
    this._onState = (event) => this.applyState(event.detail || {});
    window.addEventListener('goals-v3-capture:state', this._onState);
    window.dispatchEvent(new CustomEvent('goals-v3-capture:request-state'));
  }

  disconnect() {
    window.removeEventListener('goals-v3-capture:state', this._onState);
  }

  // The card's own button, whose click area covers the card: it checks an
  // unlanded goal, and jumps to the landed row otherwise (prototype
  // behavior). The checkbox (checkboxChanged) and the smart-paste button sit
  // beside the button, not inside it, so their clicks never reach here.
  cardClicked(event) {
    const card = event.currentTarget.closest('.goals-v3-picker-card');
    const checkbox = card && card.querySelector('input[type="checkbox"]');
    if (!checkbox) return;

    if (checkbox.checked) {
      this._jump(card.dataset.goalId);
    } else {
      checkbox.checked = true;
      this._setLanded(card.dataset.goalId, true);
    }
  }

  // The checkbox is the membership control. Unchecking is only honored while
  // the goal has nothing saved on this note; once entries are saved the goal
  // is locked into the session, so the box springs back and the click jumps
  // to the row, where the saved entries explain why.
  checkboxChanged(event) {
    const checkbox = event.currentTarget;
    if (!checkbox.checked && this._saved.has(checkbox.dataset.goalId)) {
      checkbox.checked = true;
      this._jump(checkbox.dataset.goalId);
      return;
    }
    this._setLanded(checkbox.dataset.goalId, checkbox.checked);
  }

  // Select all: every goal in, or every goal out. The capture decides what
  // leaving means for a goal holding something (it asks) or saved (it stays),
  // and its state broadcast puts this box back to what the session is.
  allChanged(event) {
    const landed = event.currentTarget.checked;
    window.dispatchEvent(new CustomEvent('goals-v3-capture:set-all', { detail: { landed } }));
  }

  applyState({ landed = [], withData = [], saved = [], total = null }) {
    this._saved = new Set(saved);
    if (this.hasAllTarget) {
      const count = this.checkboxTargets.length;
      const inSession = this.checkboxTargets.filter((checkbox) => landed.includes(checkbox.dataset.goalId)).length;
      this.allTarget.checked = count > 0 && inSession === count;
      this.allTarget.indeterminate = inSession > 0 && inSession < count;
      this.allTarget.title = this.allTarget.checked ? 'Uncheck to take every goal out of this session' : 'Check to collect data on every goal';
    }
    this.checkboxTargets.forEach((checkbox) => {
      const { goalId } = checkbox.dataset;
      const isLanded = landed.includes(goalId);
      checkbox.checked = isLanded;
      const card = checkbox.closest('.goals-v3-picker-card');
      const tag = (card && card.dataset.goalTag) || 'this goal';
      if (card) {
        card.classList.toggle('is-landed', isLanded);
        // The checkbox says whether the goal is in the session; the button
        // says what a click does, which changes once it is.
        const action = card.querySelector('.goals-v3-picker-card-action');
        if (action) {
          action.title = isLanded ? 'In this session. Click to jump to its row.' : 'Click to collect data on this goal';
        }
      }
      if (!isLanded) {
        checkbox.title = `Check to collect data on ${tag}`;
      } else if (this._saved.has(goalId)) {
        checkbox.title = `${tag} has saved data on this note. Click to jump to its row.`;
      } else {
        checkbox.title = `Uncheck to take ${tag} back out of this session`;
      }
    });
    if (this.hasSummaryTarget) {
      const count = total == null ? this.checkboxTargets.length : total;
      this.summaryTarget.textContent = `${withData.length} of ${count} goal${count === 1 ? '' : 's'} have data this session.`;
      if (this.hasSummaryIconTarget) {
        const complete = count > 0 && withData.length >= count;
        this.summaryIconTarget.classList.toggle('text-success', complete);
        this.summaryIconTarget.classList.toggle('text-muted', !complete);
      }
      if (this.hasSummaryHintTarget) {
        this.summaryHintTarget.hidden = !(count > 0 && withData.length < count);
      }
    }
  }

  _jump(goalId) {
    window.dispatchEvent(new CustomEvent('goals-v3-capture:jump', { detail: { goalId } }));
  }

  _setLanded(goalId, landed) {
    if (!goalId) return;
    window.dispatchEvent(new CustomEvent('goals-v3-capture:set', { detail: { goalId, landed } }));
  }
}
